// Settings → Cash & shifts → Counters (ADR-0032; spec/13 §13): the venue's counters a shift starts at, in the kit's
// collection pattern (`ListCard`, `ListRow`, 2026-10-07). Each counter is one row — its name, then "Default float
// ₹2,000 · PC 900" — with Edit and Archive (or Restore); adding or editing one is a small centred dialog with its
// name, its default float (in rupees) and the Clavian Counter PC there (a station with role `counter`, for its printer
// and cash drawer). A counter with a drawer open is not archived, and says why. `settings:payments` edits; anyone who
// can start a shift reads.

import type { Counter } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, ListCard, ListRow, SelectField, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';


import { useProblem } from '../api/use-problem';
import { useAccess } from '../api/queries';
import { minorToMajor, moneyFrom } from '../org/money';
import { useCounters, useCreateCounter, useUpdateCounter } from '../org/shifts/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { useCurrentVenue } from '../shell/scope';
import { useStations } from '../stations/queries';
import { Notice } from '../ui';

import './counters.css';

const SETTINGS_PAYMENTS = 'settings:payments';
const NO_PC = 'none';

interface Draft {
  readonly name: string;
  readonly float: string;
  readonly stationId: string;
}

export function CountersGroup({ organizationId, venueId }: { readonly organizationId: string; readonly venueId: string }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const currency = useCurrentVenue().currency ?? 'INR';
  const format = useVenueFormatter();
  const { show } = useToast();
  const access = useAccess({ venueId });
  const canEdit = access.status === 'ready' && access.can(SETTINGS_PAYMENTS);
  const board = useCounters(organizationId, venueId, { includeArchived: true });
  const stations = useStations(organizationId, venueId);
  const update = useUpdateCounter(organizationId, venueId);
  const [editing, setEditing] = useState<{ readonly counter: Counter | null } | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  const counterPcs = (stations.data?.pages.flatMap((page) => page.data) ?? []).filter((station) => station.role === 'counter');
  const pcOptions = [
    { value: NO_PC, label: t('settings.counters.noPc') },
    ...counterPcs.map((station) => ({ value: station.id, label: station.name ?? t('settings.counters.pcNumber', { number: station.number }) })),
  ];
  const pcName = (id: string | null) => {
    if (id === null) return null;
    const station = counterPcs.find((one) => one.id === id);
    return station === undefined ? null : (station.name ?? t('settings.counters.pcNumber', { number: station.number }));
  };

  function archive(counter: Counter, archived: boolean): void {
    setError(undefined);
    update.mutate(
      { counterId: counter.id, body: { archived } },
      {
        onSuccess: () =>
          show({ tone: 'ok', title: archived ? t('settings.counters.archivedToast', { name: counter.name }) : t('settings.counters.restoredToast', { name: counter.name }) }),
        onError: (err) => setError(problem.text(err, { fallback: t('settings.counters.failed') })),
      },
    );
  }

  // Loading: the card in its final place, the rows as a skeleton, so the section never flashes blank (design guide §6).
  if (board.data === undefined) {
    if (board.isError) return <LoadFailure error={board.error} onRetry={() => void board.refetch()} />;
    return (
      <ListCard title={t('settings.counters.listTitle')} count={1} emptyText="" listLabel={t('settings.counters.listTitle')}>
        <li aria-busy="true" role="status" aria-label={t('app.loading')} className="clav-list-row">
          <Skeleton shape="text" lines={2} />
        </li>
      </ListCard>
    );
  }

  const counters = board.data.counters;
  const active = counters.filter((counter) => !counter.archived).length;
  const archivedCount = counters.length - active;
  const summary =
    archivedCount === 0
      ? t('settings.counters.summary', { count: active })
      : t('settings.counters.summaryArchived', { active: t('settings.counters.summary', { count: active }), archived: archivedCount });
  const addButton = canEdit ? (
    <Button size="sm" variant="secondary" onClick={() => setEditing({ counter: null })}>
      {t('settings.counters.add')}
    </Button>
  ) : undefined;

  return (
    <div className="settings-counters">
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <ListCard
        title={t('settings.counters.listTitle')}
        summary={counters.length === 0 ? undefined : summary}
        count={counters.length}
        listLabel={t('settings.counters.listTitle')}
        emptyText={t('settings.counters.emptyText')}
        {...(addButton === undefined ? {} : { action: addButton })}
        footer={counters.length === 0 ? undefined : <p className="console-muted settings-counters__intro">{t('settings.counters.intro')}</p>}
      >
        {counters.map((counter) => (
          <ListRow
            key={counter.id}
            title={counter.name}
            meta={t('settings.counters.rowMeta', {
              float: format.money(counter.defaultFloat, 'noFraction'),
              pc: pcName(counter.stationId) ?? t('settings.counters.noPc'),
            })}
            muted={counter.archived}
            {...(counter.archived
              ? { badge: <Badge tone="neutral">{t('settings.counters.archived')}</Badge> }
              : counter.running !== null
                ? { badge: <Badge tone="ok">{t('shifts.counters.open')}</Badge> }
                : {})}
            {...(canEdit
              ? {
                  actions: counter.archived ? (
                    <Button variant="secondary" size="sm" aria-label={t('settings.counters.restore', { name: counter.name })} onClick={() => archive(counter, false)}>
                      {t('settings.counters.restoreShort')}
                    </Button>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" aria-label={t('settings.counters.edit', { name: counter.name })} onClick={() => setEditing({ counter })}>
                        {t('settings.counters.editShort')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={counter.running !== null}
                        aria-label={t('settings.counters.archive', { name: counter.name })}
                        onClick={() => archive(counter, true)}
                      >
                        {t('settings.counters.archiveShort')}
                      </Button>
                    </>
                  ),
                }
              : {})}
          >
            {counter.running !== null && canEdit && !counter.archived ? <p className="console-muted settings-counters__why">{t('settings.counters.drawerOpen')}</p> : null}
          </ListRow>
        ))}
      </ListCard>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="sm"
        dirty="auto"
        title={editing?.counter == null ? t('settings.counters.addTitle') : t('settings.counters.editTitle', { name: editing.counter.name })}
        description={t('settings.counters.pcHint')}
      >
        {editing === null ? null : (
          <CounterForm
            organizationId={organizationId}
            venueId={venueId}
            counter={editing.counter}
            currency={currency}
            pcOptions={pcOptions}
            onDone={() => setEditing(null)}
          />
        )}
      </Dialog>
    </div>
  );
}

function CounterForm({
  organizationId,
  venueId,
  counter,
  currency,
  pcOptions,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly counter: Counter | null;
  readonly currency: string;
  readonly pcOptions: readonly { readonly value: string; readonly label: string }[];
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const create = useCreateCounter(organizationId, venueId);
  const update = useUpdateCounter(organizationId, venueId);
  const [draft, setDraft] = useState<Draft>(
    counter === null
      ? { name: '', float: '', stationId: NO_PC }
      : { name: counter.name, float: minorToMajor(counter.defaultFloat.amount, counter.defaultFloat.currency), stationId: counter.stationId ?? NO_PC },
  );
  const [error, setError] = useState<string | undefined>(undefined);
  const fail = (err: unknown) => setError(problem.text(err, { fallback: t('settings.counters.failed') }));

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const float = draft.float.trim() === '' ? null : moneyFrom(draft.float, currency);
    if (draft.name.trim() === '' || (draft.float.trim() !== '' && float === null) || (counter !== null && float === null)) {
      setError(t('settings.counters.invalid'));
      return;
    }
    setError(undefined);
    if (counter === null) {
      create.mutate(
        { name: draft.name.trim(), ...(float === null ? {} : { defaultFloat: float }), ...(draft.stationId === NO_PC ? {} : { stationId: draft.stationId }) },
        {
          onSuccess: (created) => {
            show({ tone: 'ok', title: t('settings.counters.added', { name: created.name }) });
            onDone();
          },
          onError: fail,
        },
      );
      return;
    }
    update.mutate(
      { counterId: counter.id, body: { name: draft.name.trim(), defaultFloat: float ?? counter.defaultFloat, stationId: draft.stationId === NO_PC ? null : draft.stationId } },
      { onSuccess: onDone, onError: fail },
    );
  }

  return (
    <form className="console-form settings-counters__form" aria-label={counter === null ? t('settings.counters.addTitle') : t('settings.counters.editLabel', { name: counter.name })} noValidate onSubmit={submit}>
      {error === undefined ? null : (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
      <TextField label={t('settings.counters.name')} required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
      <TextField
        label={t('settings.counters.float', { currency })}
        showOptional={counter === null}
        required={counter !== null}
        numeric
        inputMode="decimal"
        value={draft.float}
        onChange={(event) => setDraft({ ...draft, float: event.target.value })}
      />
      <SelectField label={t('settings.counters.pc')} value={draft.stationId} onValueChange={(value) => setDraft({ ...draft, stationId: value })} options={pcOptions} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending || update.isPending}>
          {counter === null ? t('settings.counters.add') : t('settings.counters.save')}
        </Button>
      </div>
    </form>
  );
}
